import type { PerformanceMeasure, ScenarioPerformanceData } from '@ms/yammer-telemetry';
import {
  endAndReportPerformanceMeasure,
  endAndReportPerformanceMeasureAsync,
  requestAnimationFrameAfterStyleAndLayout,
  startPerformanceMeasure,
} from '@ms/yammer-telemetry';
import type {
  PageLoadPerformanceEventProperties,
  PerformanceEventName,
  PerformanceEventProperties,
} from '@ms/yammer-telemetry-support';

import { emitPageLoadEvent } from './loadEvents';

type PartialPageLoadPerformanceMeasure = PerformanceMeasure<Partial<PageLoadPerformanceEventProperties>>;
type MostRecentPageLoadPerformanceMeasure = PerformanceMeasure<
  Partial<
    Omit<
      PageLoadPerformanceEventProperties,
      'timedOut' | 'endedWithError' | 'interrupted' | 'isExpectedError' | 'isExpectedTerminalState'
    > & {
      readonly endedWithError: undefined;
      readonly timedOut: undefined;
      readonly interrupted: undefined;
      readonly isExpectedError: undefined;
      readonly isExpectedTerminalState: undefined;
    }
  >
>;

type PageLoadPerformanceEventName = Extract<PerformanceEventName, 'page_load' | 'cached_first_page_load'>;
interface PageLoadPerformanceMeasureStore {
  readonly measures: Record<string, ScenarioLoadPerformanceData>;
  readonly completed: Set<string>;
  // oxlint-disable-next-line @ms/prefer-readonly-properties
  mostRecentPageLoadMeasure?: MostRecentPageLoadPerformanceMeasure;
}

/**
 * Active `measures` are removed as soon as they are reported or cancelled. The `completed` set only guards against
 * duplicate reporting (a late timeout or scheduled paint firing after an event has already been reported), so its
 * entries are short-lived in practice. Because page load ids increase monotonically the set would otherwise grow
 * for the lifetime of the tab, so we retain only the most recent entries to keep memory usage bounded. The cap is
 * far above the handful of page loads that can ever overlap, so no still-relevant guard is dropped.
 */
const store: PageLoadPerformanceMeasureStore = { measures: {}, completed: new Set() };

// Scenario timeout (30 seconds)
const pageLoadMeasureTimeout = 30_000;

// Upper bound on retained `completed` guards. Page loads are sequential, so only a couple can ever be in flight at
// once; this leaves a wide safety margin while preventing unbounded growth over a long-lived tab.
const maxRetainedCompletedMeasures = 50;

type PartialPageLoadPerformanceEventProperties = PerformanceEventProperties<
  Partial<PageLoadPerformanceEventProperties>
>;

interface PartialPageLoadPerformanceMeasureOptions {
  readonly eventName: PageLoadPerformanceEventName;
  readonly pageLoadId: number;
  readonly eventProperties?: PartialPageLoadPerformanceEventProperties;
}
type UpdatePageLoadPerformanceMeasure = (options: PartialPageLoadPerformanceMeasureOptions) => void;

type StartPageLoadPerformanceMeasure = (options: PartialPageLoadPerformanceMeasureOptions) => void;

/**
 * Starts a page performance measure event, which can be ended asynchronously by its pageLoadId. Events will cancel
 * and report as "timedOut" after 30 seconds. Uses startPerformanceMeasure and endScenarioPerformanceMeasure
 * internally to execute the performance measurements.
 * Returns true if successfully started, false if the event was already started.
 */
export const startPageLoadPerformanceMeasure: StartPageLoadPerformanceMeasure = ({
  eventName,
  pageLoadId,
  eventProperties = {},
}) => {
  const isMeasureActiveForPageLoadId = getPerformanceEntry(eventName, pageLoadId);
  if (isMeasureActiveForPageLoadId) {
    return;
  }

  const isMeasureCompletedForPageLoadId = isPerformanceEventMeasureMarkedAsCompleted(eventName, pageLoadId);
  if (isMeasureCompletedForPageLoadId) {
    return;
  }

  // Start and store measure in scenario collection
  const measure: PartialPageLoadPerformanceMeasure = {
    ...startPerformanceMeasure(eventName),
    eventProperties,
  };
  const entry: ScenarioLoadPerformanceData = {
    measure,
    timer: window.setTimeout(async () => {
      const eventProperties: PerformanceEventProperties<
        Pick<PageLoadPerformanceEventProperties, 'timedOut' | 'endedWithError' | 'interrupted'>
      > = {
        timedOut: true,
        endedWithError: false,
        interrupted: false,
      };

      // istanbul ignore next - Defensive check: prevent duplicate reporting if measure completed before timeout fires
      // This path is exercised in tests but coverage tools struggle with async setTimeout callbacks
      if (isPerformanceEventMeasureMarkedAsCompleted(eventName, pageLoadId)) {
        return;
      }

      await endAndReportPageLoadPerformanceMeasureNowAsync({
        eventName,
        pageLoadId,
        eventProperties,
      });
    }, pageLoadMeasureTimeout),
  };

  setPerformanceEventEntry(eventName, pageLoadId, entry);

  if (eventName === 'page_load') {
    setMostRecentPageLoadMeasure(measure);
  }
};

/**
 * Updates event properties of an in-progress scenario performance measure. This allows for correct reporting of
 * dynamic properties (like didVisibilityStateChange) in case the event times out.
 */
export const updatePageLoadPerformanceMeasureEventProperties: UpdatePageLoadPerformanceMeasure = ({
  eventName,
  pageLoadId,
  eventProperties = {},
}) => {
  const entry = getPerformanceEntry(eventName, pageLoadId);

  if (entry) {
    const measure = {
      ...entry.measure,
      eventProperties: {
        ...(entry.measure.eventProperties as PageLoadPerformanceEventProperties),
        ...eventProperties,
      },
    };

    const updatedData: ScenarioLoadPerformanceData = {
      ...entry,
      measure,
    };

    setPerformanceEventEntry(eventName, pageLoadId, updatedData);

    // istanbul ignore else
    if (eventName === 'page_load') {
      setMostRecentPageLoadMeasure(measure);
    }
  }
};

export const startOrUpdatePageLoadPerformanceMeasureEventProperties: StartPageLoadPerformanceMeasure = ({
  eventName,
  pageLoadId,
  eventProperties,
}) => {
  const entry = getPerformanceEntry(eventName, pageLoadId);

  if (entry) {
    updatePageLoadPerformanceMeasureEventProperties({ eventName, pageLoadId, eventProperties });
  } else {
    startPageLoadPerformanceMeasure({ eventName, pageLoadId, eventProperties });
  }
};

type EndAndReportPageLoadPerformanceMeasure = (options: PartialPageLoadPerformanceMeasureOptions) => void;
type EndAndReportPageLoadPerformanceMeasureAsync = (options: PartialPageLoadPerformanceMeasureOptions) => Promise<void>;

/**
 * Schedules a scenario performance event to be ended and reported after the full render + paint. Although the event
 * will be reported asynchronously to account for real user experience, it will be immediately tracked as "reported"
 * to prevent other async code from canceling or reporting this event.
 */
export const scheduleEndAndReportPageLoadPerformanceMeasureAfterStyleAndLayout: EndAndReportPageLoadPerformanceMeasure =
  (options) => {
    markPerformanceEventEntryAsCompleted(options.eventName, options.pageLoadId);

    requestAnimationFrameAfterStyleAndLayout(() => {
      endAndReportPageLoadPerformanceMeasureNow(options);
    });
  };

/**
 * Ends a scenario performance event and reports it to telemetry. It may be called automatically if a timeout is
 * reached (timedOut) or if a new scenario with the same event name is started (interrupted).
 */
export const endAndReportPageLoadPerformanceMeasureNowIfNotCompleted: EndAndReportPageLoadPerformanceMeasure = (
  options
) => {
  if (isPerformanceEventMeasureMarkedAsCompleted(options.eventName, options.pageLoadId)) {
    return;
  }

  endAndReportPageLoadPerformanceMeasureNow(options);
};

/**
 * Ends a scenario performance event and reports it to telemetry immediately, even if already marked as completed.
 */
const endAndReportPageLoadPerformanceMeasureNow: EndAndReportPageLoadPerformanceMeasure = ({
  eventName,
  pageLoadId,
  eventProperties = {},
}) => {
  const scenarioData = getPerformanceEntry(eventName, pageLoadId);

  if (scenarioData) {
    const mergedEventProperties = {
      ...scenarioData.measure.eventProperties,
      ...eventProperties,
    };
    const measure: PartialPageLoadPerformanceMeasure = {
      ...scenarioData.measure,
      eventProperties: mergedEventProperties,
    };

    endAndReportPerformanceMeasure(measure);
    clearPerformanceEventEntry(eventName, pageLoadId, scenarioData);
    markPerformanceEventEntryAsCompleted(eventName, pageLoadId);

    // istanbul ignore else
    if (eventName === 'page_load') {
      setMostRecentPageLoadMeasure(measure);
    }

    triggerPageLoadEvent(mergedEventProperties, pageLoadId);
  }
};

/**
 * Async variant that awaits the telemetry event to be fully processed and enqueued before triggering
 * the page load event. Use this when you need to ensure the event is in the telemetry queue before
 * proceeding (e.g., before flushing telemetry or before the application context is destroyed).
 */
const endAndReportPageLoadPerformanceMeasureNowAsync: EndAndReportPageLoadPerformanceMeasureAsync = async ({
  eventName,
  pageLoadId,
  eventProperties,
}) => {
  const scenarioData = getPerformanceEntry(eventName, pageLoadId);

  // istanbul ignore else - Defensive check: scenarioData should always exist when this function is called from timeout
  if (scenarioData) {
    const measure: PartialPageLoadPerformanceMeasure & {
      readonly eventProperties: Partial<PageLoadPerformanceEventProperties>;
    } = {
      ...scenarioData.measure,
      eventProperties: {
        ...scenarioData.measure.eventProperties,
        ...eventProperties,
      },
    };

    await endAndReportPerformanceMeasureAsync(measure);
    clearPerformanceEventEntry(eventName, pageLoadId, scenarioData);
    markPerformanceEventEntryAsCompleted(eventName, pageLoadId);

    // istanbul ignore else
    if (eventName === 'page_load') {
      setMostRecentPageLoadMeasure(measure);
    }

    triggerPageLoadEvent(measure.eventProperties, pageLoadId);
  }
};

const getKey = (eventName: PageLoadPerformanceEventName, pageLoadId: number) => `${eventName}-${pageLoadId}`;

type ScenarioLoadPerformanceData = ScenarioPerformanceData<Partial<PageLoadPerformanceEventProperties>>;

const clearPerformanceEventEntry = (
  eventName: PageLoadPerformanceEventName,
  pageLoadId: number,
  scenarioData: ScenarioLoadPerformanceData
) => {
  window.clearTimeout(scenarioData.timer);

  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete store.measures[getKey(eventName, pageLoadId)];
};

type GetPerformanceEventEntry = (
  eventName: PageLoadPerformanceEventName,
  pageLoadId: number
) => ScenarioLoadPerformanceData | undefined;
const getPerformanceEntry: GetPerformanceEventEntry = (eventName, pageLoadId) =>
  store.measures[getKey(eventName, pageLoadId)];

const setPerformanceEventEntry = (
  eventName: PageLoadPerformanceEventName,
  pageLoadId: number,
  entry: ScenarioLoadPerformanceData
) => {
  store.measures[getKey(eventName, pageLoadId)] = entry;
};

const markPerformanceEventEntryAsCompleted = (eventName: PageLoadPerformanceEventName, pageLoadId: number) => {
  store.completed.add(getKey(eventName, pageLoadId));
  pruneOldCompletedMeasures();
};

const pruneOldCompletedMeasures = () => {
  const excess = store.completed.size - maxRetainedCompletedMeasures;
  if (excess <= 0) {
    return;
  }

  Array.from(store.completed)
    .slice(0, excess)
    .forEach((key) => store.completed.delete(key));
};

interface ClearCompletedPerformanceEventMeasureOptions {
  readonly eventName: PageLoadPerformanceEventName;
  readonly pageLoadId: number;
}
type ClearCompletedPerformanceEventMeasure = (props: ClearCompletedPerformanceEventMeasureOptions) => void;
export const clearCompletedPerformanceEventMeasure: ClearCompletedPerformanceEventMeasure = ({
  eventName,
  pageLoadId,
}) => {
  store.completed.delete(getKey(eventName, pageLoadId));
};

type IsPerformanceEventMeasureMarkedAsCompleted = (
  eventName: PageLoadPerformanceEventName,
  pageLoadId: number
) => boolean;
const isPerformanceEventMeasureMarkedAsCompleted: IsPerformanceEventMeasureMarkedAsCompleted = (
  eventName,
  pageLoadId
) => store.completed.has(getKey(eventName, pageLoadId));

export const getMostRecentPageLoadMeasure = (): MostRecentPageLoadPerformanceMeasure | undefined =>
  store.mostRecentPageLoadMeasure;
const setMostRecentPageLoadMeasure = (measure: PartialPageLoadPerformanceMeasure) => {
  store.mostRecentPageLoadMeasure = {
    ...measure,
    eventProperties: {
      ...measure.eventProperties,
      endedWithError: undefined,
      timedOut: undefined,
      interrupted: undefined,
      isExpectedError: undefined,
      isExpectedTerminalState: undefined,
    },
  };
};

type TriggerPageLoadEvent = (data: PartialPageLoadPerformanceEventProperties, pageLoadId: number) => void;
const triggerPageLoadEvent: TriggerPageLoadEvent = (data, pageLoadId) => {
  const eventData = {
    ...data,
    page: data.page || 'Unknown',
    urlPath: data.urlPath || 'unknown',
    hero: data.hero || 'unknown',
    pageLoadId,
  };
  if (data.timedOut) {
    emitPageLoadEvent('TimedOut', eventData);
  } else if (data.interrupted) {
    emitPageLoadEvent('Interrupted', eventData);
  } else if (data.endedWithError) {
    if (data.isExpectedTerminalState) {
      emitPageLoadEvent('FailedExpected', eventData);
    } else if (data.isExpectedError) {
      emitPageLoadEvent('FailedRecoverable', eventData);
    } else {
      emitPageLoadEvent('Failed', eventData);
    }
  } else {
    emitPageLoadEvent('Loaded', eventData);
  }
};
